import { BufferGeometry } from "three";
import { Megabytes } from "../globals";
import { BufferManagerWebGPU } from './BufferManagerWebGPU';
/** @import { Renderer } from "./Renderer" */
/** @import { StagingBuffers } from "./StagingBuffers" */
/** @import { RenderBatch } from "../scene/RenderBatch" */

// 6 vertices per point: two triangles forming a screen-space quad (see basic.wgsl).
const VERTICES_PER_POINT = 6;

/**
 * Manages GPU storage buffers (SSBOs) for point geometry.
 *
 * Point geometry is rendered without vertex/index buffers: positions (and optional colors) are
 * packed into a shared storage buffer and the vertex shader reads them via vertex_index, expanding
 * each point into a screen-space quad (see basic.wgsl, UberPipeline). This is conceptually separate
 * from VertexBuffer, but shares its StagingBuffers (injected) so both use a single per-frame upload
 * path. Lives at the same hierarchical level as VertexBuffer and is owned by the Renderer.
 */
export class PointDataBuffer {

  /** @type {Renderer} */
  #renderer;
  /** @type {StagingBuffers} */
  #staging;

  /** @type {BufferManagerWebGPU} */
  #uploadStorageBufferManager;
  /** @type {BufferManagerWebGPU} */
  #streamStorageBufferManager;
  /** @type {number} */
  #storageAlignment;

  /** @type {Set.<BufferGeometry>} */
  #streamDraws = new Set();

  /**
   * @param {Renderer} renderer
   * @param {StagingBuffers} staging Shared staging buffers used for the per-frame upload path.
   */
  constructor(renderer, staging) {
    this.#renderer = renderer;
    this.#staging = staging;

    const device = renderer.getDevice();

    const storageBufferDesc = {
      label: 'storage-buffer',
      size: Megabytes(16),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    };

    this.#uploadStorageBufferManager = new BufferManagerWebGPU(device, storageBufferDesc, { freesAreBatched: true });
    this.#streamStorageBufferManager = new BufferManagerWebGPU(device, storageBufferDesc, { freesAreBatched: true });
    this.#storageAlignment = device.limits.minStorageBufferOffsetAlignment;
  }

  static #disposeHandler(event) {
    const geometry = event.target;
    geometry.removeEventListener('dispose', PointDataBuffer.#disposeHandler);
    PointDataBuffer.deallocateGeometry(geometry);
  }

  /**
   * Free up the point storage buffer allocation for this geometry, if there is one.
   * free() is batched and the underlying GPUBuffer is only destroyed (via flushFreeBlocks)
   * after all GPU commands have been submitted.
   * @param {BufferGeometry} geometry
   */
  static deallocateGeometry(geometry) {
    if (!geometry.__gpuPointSSBOBlock) {
      return;
    }
    geometry.__gpuPointBufferManager?.free(geometry.__gpuPointSSBOBlock);
    geometry.__gpuPointSSBO = undefined;
    geometry.__gpuPointSSBOBlock = undefined;
    geometry.__gpuPointSSBOOffset = undefined;
    geometry.__gpuPointSSBOSize = undefined;
    geometry.__gpuPointCount = undefined;
    geometry.__gpuPointDataBindGroup = undefined;
    geometry.__gpuPointBufferManager = undefined;
  }

  /**
   * @param {BufferGeometry} geom Owner of the copy, tracked so updateBufferRange can detect a
   *   not-yet-flushed staged copy and defer the pull-up sync to a full re-upload.
   * @param {GPUBuffer} dstBuffer
   * @param {number} dstOffset
   * @param {ArrayBuffer} srcBuffer
   * @param {number} srcOffset
   * @param {number} size
   */
  #writeBuffer(geom, dstBuffer, dstOffset, srcBuffer, srcOffset, size) {
    // If the staging buffers are full, fallback to GPUQueue.writeBuffer(). The fallback is
    // not tracked: it is enqueued immediately, so it cannot race ahead of a pending copy.
    if (!this.#staging.writeData(dstBuffer, dstOffset, srcBuffer, srcOffset, size, geom)) {
      this.#renderer.getDevice().queue.writeBuffer(dstBuffer, dstOffset, srcBuffer, srcOffset, size);
    }
  }

  /**
   * Upload point geometry into a shared storage buffer (SSBO).
   *
   * All point state is tracked via the dedicated __gpuPoint* fields, including
   * __gpuPointBufferManager which #clearStreamBuffers uses to identify geometry owned by the
   * stream allocator. Point geometry does not use vertex/index buffers, so it does not set
   * __gpuvb; residency is tracked point-aware via __gpuPointSSBO.
   *
   * @param {BufferGeometry} geometry
   * @param {boolean} useStreamAllocator
   */
  #initSSBO(geometry, useStreamAllocator) {
    // Already uploaded and unchanged.
    if (geometry.__gpuPointSSBO && !geometry.vbNeedsUpdate) {
      return;
    }

    // Data changed: free the previous allocation before re-uploading.
    if (geometry.__gpuPointSSBOBlock) {
      PointDataBuffer.deallocateGeometry(geometry);
    }

    const storageBufferManager = useStreamAllocator ?
    this.#streamStorageBufferManager :
    this.#uploadStorageBufferManager;

    const stride = geometry.vbstride;
    const hasIB = !!geometry.ib;
    const numPoints = hasIB ? geometry.ib.length : geometry.vb.length / stride;
    const dataByteLength = numPoints * stride * 4;
    const block = storageBufferManager.allocate(dataByteLength, this.#storageAlignment);

    if (hasIB) {
      // Pack vertex data in index buffer order.
      const srcVB = geometry.vb;
      const srcIB = geometry.ib;
      const packedData = new Float32Array(numPoints * stride);
      for (let i = 0; i < numPoints; i++) {
        const srcOffset = srcIB[i] * stride;
        const dstOffset = i * stride;
        for (let f = 0; f < stride; f++) {
          packedData[dstOffset + f] = srcVB[srcOffset + f];
        }
      }
      this.#writeBuffer(geometry, block.buffer, block.offset, packedData.buffer, 0, dataByteLength);
    } else {
      // Without an index buffer, just write the vertex buffer to the SSBO.
      this.#writeBuffer(geometry, block.buffer, block.offset, geometry.vb.buffer, geometry.vb.byteOffset, dataByteLength);
    }

    geometry.__gpuPointSSBO = block.buffer;
    geometry.__gpuPointSSBOBlock = block;
    geometry.__gpuPointSSBOOffset = block.offset;
    geometry.__gpuPointSSBOSize = dataByteLength;
    geometry.__gpuPointCount = numPoints;
    geometry.__gpuPointBufferManager = storageBufferManager;
    geometry.vbNeedsUpdate = false;

    if (!useStreamAllocator) {
      geometry.addEventListener('dispose', PointDataBuffer.#disposeHandler);
    }
  }

  /**
   * @param {BufferGeometry} geom
   */
  initUploaded(geom) {
    if (this.#streamDraws.has(geom)) {
      PointDataBuffer.deallocateGeometry(geom);
      this.#streamDraws.delete(geom);
    }
    this.#initSSBO(geom, false);
  }

  /**
   * @param {BufferGeometry} geom
   */
  initStream(geom) {
    if (this.#streamDraws.has(geom)) {
      return;
    }
    this.#initSSBO(geom, true);
    this.#streamDraws.add(geom);
  }

  /**
   * Mark point geometry for a full GPU re-upload on the next draw.
   * Invalidates cached render bundles so ensurePointData runs instead of replaying a
   * bundle that was recorded before pull-up updated the CPU buffer.
   * @param {BufferGeometry} geom
   * @param {RenderBatch[]} [renderBatches] - Batches to invalidate when known by the caller.
   */
  #markPointDataDirty(geom, renderBatches) {
    if (geom.vbNeedsUpdate) {
      return;
    }
    geom.vbNeedsUpdate = true;
    this.#renderer.invalidateRenderBundlesForBatches(renderBatches);
  }

  /**
   * Update a range of vertices and/or indices in an already uploaded point geometry.
   * For point geometry with an index buffer, updating any vertex data will fall back to a full re-upload.
   *
   * Note currently, calling this twice in the same frame might cause a full-reupload, since we are staging
   * the changes and cannot update them after staging.
   *
   * @param {BufferGeometry} geom
   * @param {number} [vertexStart] - Start vertex index
   * @param {number} [vertexEnd] - End vertex index (exclusive)
   * @param {number} [indexStart] - Start index in index buffer
   * @param {number} [indexEnd] - End index in index buffer (exclusive)
   * @param {RenderBatch[]} [renderBatches] - Batches to invalidate on full re-upload fallback.
   */
  updateBufferRange(geom, vertexStart, vertexEnd, indexStart, indexEnd, renderBatches) {
    // For streaming geometries, just mark as needing update.
    // The streaming system will re-allocate and upload from CPU memory on next draw.
    if (geom.streamingDraw) {
      // No need to invalidate render bundles for streaming geometries
      // since we only generate those for uploaded geometries
      geom.vbNeedsUpdate = true;
      return;
    }

    // Full re-upload already pending — ensurePointData will upload on next draw.
    if (geom.vbNeedsUpdate) {
      return;
    }

    // Staged uploads are only flushed before draw. Defer pull-up GPU sync until the
    // SSBO is allocated and this geometry's own pending staged copy has been flushed.
    if (!geom.__gpuPointSSBOBlock ||
    this.#staging.hasPendingCopy(geom)) {
      this.#markPointDataDirty(geom, renderBatches);
      return;
    }

    const stride = geom.vbstride;
    const strideBytes = stride * 4;

    if (geom.ib) {
      const hasVertexRange = vertexStart !== undefined && vertexEnd !== undefined;
      const hasIndexRange = indexStart !== undefined && indexEnd !== undefined;
      // If vertices changed, any index could reference a modified vertex -> full re-upload.
      // Without an index range we don't know where in the destination buffer to write -> full re-upload.
      if (hasVertexRange || !hasIndexRange) {
        this.#markPointDataDirty(geom, renderBatches);
        return;
      }

      // If only indices changed, we can re-pack just the affected range in the destination buffer.
      const count = indexEnd - indexStart;
      const packedData = new Float32Array(count * stride);
      const srcVB = geom.vb;
      const srcIB = geom.ib;

      for (let i = 0; i < count; i++) {
        const srcOffset = srcIB[indexStart + i] * stride;
        const dstOffset = i * stride;
        for (let f = 0; f < stride; f++) {
          packedData[dstOffset + f] = srcVB[srcOffset + f];
        }
      }

      const byteOffset = geom.__gpuPointSSBOOffset + indexStart * strideBytes;
      this.#writeBuffer(geom, geom.__gpuPointSSBO, byteOffset, packedData.buffer, 0, count * strideBytes);
    } else {
      // Without an index buffer, write the vertex range directly
      if (vertexStart === undefined || vertexEnd === undefined) {
        this.#markPointDataDirty(geom, renderBatches);
        return;
      }

      const count = vertexEnd - vertexStart;
      const size = count * strideBytes;
      const dstOffset = geom.__gpuPointSSBOOffset + vertexStart * strideBytes;
      const srcOffset = geom.vb.byteOffset + vertexStart * strideBytes;

      this.#writeBuffer(geom, geom.__gpuPointSSBO, dstOffset, geom.vb.buffer, srcOffset, size);
    }
  }

  /**
   * Ensure the point data SSBO is created and up-to-date for the given geometry.
   * Must be called before creating a bind group for the point data.
   * @param {BufferGeometry} geometry
   */
  ensurePointData(geometry) {
    if (!geometry.__gpuPointSSBO || geometry.vbNeedsUpdate) {
      if (geometry.streamingDraw) {
        this.initStream(geometry);
      } else {
        this.initUploaded(geometry);
      }
    }
  }

  /**
   * Issue the draw call for point geometry. The SSBO and bind group must already be set up.
   * @param {StateTrackingEncoder} passEncoder
   * @param {BufferGeometry} geometry
   * @param {number} objectIndex - object uniform index (passed as firstInstance)
   */
  drawPoints(passEncoder, geometry, objectIndex) {
    // TODO (expected ~2027, once wgslLanguageFeatures has broad support for the
    // "immediate_address_space" feature, i.e. passEncoder.setImmediates(..)):
    // Optimize point rendering by switching to instanced indexed quad drawing.
    // - The objectIndex can be passed in as an immediate instead of firstInstance,
    //   freeing up the @builtin(instance_index) to be used for proper instanced drawing.
    // - Use a tiny shared static index buffer ([0,1,2, 2,1,3]) and drawIndexed(6, numPoints, ...)
    //   so each point is one instance. The GPU reuses the 4 corner vertices instead of running
    //   the vertex shader 6x/point -> ~33% fewer vertex shader invocations.
    // - The point data should then become a per-instance vertex buffer (stepMode: 'instance')
    //   rather than an SSBO: firstInstance selects the geometry's base offset for free, and
    //   normalized vertex formats (unorm8x4 colors, unorm16 positions) replace the manual
    //   unpacking. Points then route through the standard VertexBuffer path.
    // - Because the SSBO goes away, the dedicated point-data bind group (group 3, see
    //   point_data_uniforms.wgsl / UberPipeline.#getOrCreatePointDataBindGroup) becomes
    //   unnecessary and can be removed, eliminating the per-draw setBindGroup(3) call.
    const numPoints = geometry.__gpuPointCount;
    const vertexCount = numPoints * VERTICES_PER_POINT;
    passEncoder.draw(vertexCount, 1, 0, objectIndex);
  }

  /**
   * @param {BufferGeometry} geometry
   * @returns {boolean} Whether the point geometry can be drawn (uploaded) this frame.
   */
  canDraw(geometry) {
    const needsUpload = !geometry.__gpuPointSSBO || geometry.vbNeedsUpdate;
    if (!needsUpload) {
      return true;
    }
    // Same size computation as in #initSSBO
    const stride = geometry.vbstride;
    const numPoints = geometry.ib ? geometry.ib.length : geometry.vb.length / stride;
    const neededByteLength = numPoints * stride * 4;
    // Point uploads share the per-frame budget tracked by VertexBuffer.
    return this.#renderer.canUpload(neededByteLength);
  }

  /**
   * Free up all blocks in the stream allocator.
   */
  clearStreamBuffers() {
    for (const geom of this.#streamDraws) {
      if (geom.__gpuPointBufferManager === this.#streamStorageBufferManager) {
        PointDataBuffer.deallocateGeometry(geom);
      }
    }
    this.#streamDraws.clear();
    this.#streamStorageBufferManager.flushFreeBlocks();
  }

  /**
   * Free up unneeded memory. Should be called at the end of each frame.
   */
  cleanup() {
    this.#uploadStorageBufferManager.flushFreeBlocks();
    this.clearStreamBuffers();
  }

  /**
   * Get the total amount of point storage memory in use.
   * @returns {number}
   */
  getTotalMemory() {
    return this.#uploadStorageBufferManager.getTotalSize() +
    this.#streamStorageBufferManager.getTotalSize();
  }
}